import type { QueryClient } from "@tanstack/react-query";

import { clearToasts } from "@/components/shell/toast";

/**
 * What to do with everything cached when the person using this tab changes —
 * on sign-in, and (below, the other two functions) on sign-out.
 *
 * Everything cached belongs to whoever was signed in before: a second person
 * signing in on one browser must not see the first one's orders sitting in the
 * cache. So it is all dropped — but by **resetting** the queries, not by
 * `queryClient.clear()`, and the difference is the bug of #498.
 *
 * `clear()` removes the queries from the cache and leaves every component that
 * is already mounted holding a query that is no longer in it. Nothing tells
 * those components, and they do not look again until they are re-created. The
 * one that matters is `AppPermissions`, which sits above the router and so is
 * mounted before anyone has signed in: it asked `/api/me` while signed out, got
 * a 401, and after `clear()` was still listening to that answer. Signing in
 * never reached it, so for the rest of the session the shell decided what to
 * show from the signed-out fallback set — which holds only the navigation's
 * keys, not `comments.create` or `orders.detail.cancel` or the other
 * permissions a screen checks. An Administrator opened an order and was shown
 * its comments with no box to write in, and no Cancel button, until they
 * reloaded the page.
 *
 * `resetQueries()` keeps each query where it is, throws away its data and
 * refetches the ones something is showing — so a mounted observer gets the new
 * person's answer. The promise settles when those refetches have, which is what
 * lets a sign-in wait for the permissions before it navigates and not draw the
 * shell once with the fallback.
 *
 * The toasts are dropped here too (`clearToasts`): one raised for the person
 * who was here, whether before they signed out or by a write of theirs that
 * answered after, is not for this one (#523).
 */
export function resetCacheForNewSession(
  queryClient: QueryClient,
): Promise<void> {
  clearToasts();
  return queryClient.resetQueries();
}

/**
 * Sign-out, first half: nothing may be in flight when the session ends.
 *
 * Run before the sign-out request. A read that was out when the cookie went
 * would come back 401 — and by the time anyone looked, that was a console full
 * of failures for a person who had done nothing wrong. Cancelling puts each
 * query back where it was before the fetch started, so none of them ends up
 * holding an error.
 */
export function stopQueriesBeforeSignOut(
  queryClient: QueryClient,
): Promise<void> {
  return queryClient.cancelQueries();
}

/**
 * Sign-out, second half: forget everything, and ask for nothing.
 *
 * Not `resetCacheForNewSession`, on purpose. `resetQueries()` is right at
 * sign-in, where a mounted observer must be handed the new person's answer. At
 * sign-out it refetches every query a screen is still showing — and the screen
 * is still there, for the moment before the caller navigates away — with the
 * session already gone: five requests answering 401 from Orders, a dozen from
 * Tasks (#523).
 *
 * This is the first half of what `resetQueries()` does and none of the second:
 * each query is put back to having no data, which is what stops the next
 * person seeing the last one's, and nothing is fetched. Done on the queries
 * themselves rather than by removing them, so an observer that is still
 * mounted — `AppPermissions` always is — is not left holding a query that is no
 * longer in the cache (#498). The next sign-in refetches what is mounted.
 *
 * The toasts go too: see `clearToasts`.
 */
export function forgetCacheAfterSignOut(queryClient: QueryClient): void {
  for (const query of queryClient.getQueryCache().findAll()) {
    query.reset();
  }
  clearToasts();
}
